import { MapPin, Clock, MoreVertical } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

interface AssignmentCardProps {
  assignment: any;
  crewColor: string;
  onStatusUpdate: (id: string, status: string) => void;
}

const STATUS_CONFIG = {
  scheduled: { variant: "default" as const, label: "Scheduled", color: "bg-primary/10 text-primary" },
  in_progress: { variant: "secondary" as const, label: "In Progress", color: "bg-warning/10 text-warning" },
  completed: { variant: "outline" as const, label: "Completed", color: "bg-success/10 text-success" },
  cancelled: { variant: "destructive" as const, label: "Cancelled", color: "bg-destructive/10 text-destructive" },
};

export function AssignmentCard({ assignment, crewColor, onStatusUpdate }: AssignmentCardProps) {
  const status = STATUS_CONFIG[assignment.status as keyof typeof STATUS_CONFIG] || STATUS_CONFIG.scheduled;

  return (
    <div className={`premium-card p-4 border-l-4 ${crewColor}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex-1 space-y-2">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold">{assignment.crew?.name}</h3>
            <Badge variant={status.variant} className="text-xs">
              {status.label}
            </Badge>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <MapPin className="w-4 h-4 flex-shrink-0" />
            <span className="line-clamp-1">{assignment.job?.property_address}</span>
          </div>
          
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Clock className="w-4 h-4 flex-shrink-0" />
            <span>{assignment.start_time} - {assignment.end_time}</span>
          </div>
        </div>

        <div className="flex items-center gap-2">
          {assignment.status === "scheduled" && (
            <Button
              size="sm"
              onClick={() => onStatusUpdate(assignment.id, "in_progress")}
              className="text-xs"
            >
              Start
            </Button>
          )}
          {assignment.status === "in_progress" && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => onStatusUpdate(assignment.id, "completed")}
              className="text-xs"
            >
              Complete
            </Button>
          )}
          
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                <MoreVertical className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onStatusUpdate(assignment.id, "cancelled")}>
                Cancel Assignment
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </div>
  );
}
